import type { AdditionalFundsRequiredDto } from '@/api/backend/public';
import type { ApplicationDto, UtmMetricDto } from '@/api/backend/users/applications';
import { ErhlEquityAccessMethod } from '@/helpers/const';
import { buildErhlDrawdowns } from '@/mappers/mapFromApi/application-progress';
import { mapFromApi } from '@/mappers';
import type {
  Applicant,
  ApplicantFinancials,
  ApplicantId,
  ErhlDrawdown,
  Financials,
  IncomingProperty,
  LoanApplication,
  LoanTypeEnum,
} from '@/types';

/**
 * Copies the application-level Equity Release purchase contribution onto the first incoming property.
 * The summary and the outbound mapper both read that first property. Putting the total on every
 * property would count it once per property.
 */
const withErhlPurchaseContribution = (
  data: ApplicationDto,
  incomingProperties: IncomingProperty[],
): IncomingProperty[] => {
  const firstProperty = incomingProperties[0];
  if (data.lendingProduct !== 'erhl' || !firstProperty) return incomingProperties;

  const savings = data.calculatedValue?.savingsToContributeToPurchase;
  const gifted = data.additionalFunds?.giftedFundsAmount;
  if (savings == null && gifted == null) return incomingProperties;

  return incomingProperties.map((property, index) =>
    index === 0
      ? {
          ...property,
          ...(savings != null ? { erhlSavingsAmount: savings } : {}),
          ...(gifted != null ? { erhlGiftedAmount: gifted } : {}),
        }
      : property,
  );
};

/**
 * Lump sum only, unless a positive scheduled equity release drawdown is already stored.
 */
const erhlEquityAccessMethod = (data: ApplicationDto, drawdowns: ErhlDrawdown[] | undefined) => {
  if (data.lendingProduct !== 'erhl') return undefined;
  const hasScheduledRelease = (drawdowns ?? []).some(
    (drawdown) => drawdown.drawdownType === 'scheduled_equity_release' && (Number(drawdown.amount) || 0) > 0,
  );
  return hasScheduledRelease
    ? ErhlEquityAccessMethod.LUMP_SUM_AND_MONTHLY_DRAWDOWN
    : ErhlEquityAccessMethod.LUMP_SUM_ONLY;
};

const mapFinancials = (applicantId: string, application: ApplicationDto): Financials => {
  return {
    income: mapFromApi.income(applicantId, application),
    expenses: mapFromApi.expenses(applicantId, application),
    assets: mapFromApi.assets(applicantId, application),
    liabilities: mapFromApi.liabilities(applicantId, application),
  };
};

export const application = (data: ApplicationDto): LoanApplication => {
  const applicants = data.applicants
    .map(mapFromApi.applicant)
    .sort((applicant) => (applicant.type === 'primary' ? -1 : 1));

  // The backend marks an applicant declared (isApplicantDeclared) once their I&E/declaration is
  // submitted. The application-level declaredApplicantIds array isn't always returned, so union it
  // with the per-applicant flags — this is the authoritative "submitted → read-only" signal every
  // flow relies on.
  const declaredPerApplicant = (data.applicants ?? [])
    .filter((applicant) => applicant.isApplicantDeclared)
    .map((applicant) => applicant.id)
    .filter((id): id is string => !!id);

  const declaredApplicantIds: ApplicantId[] = [
    ...new Set([...(data.declaredApplicantIds ?? []), ...declaredPerApplicant]),
  ];
  const financials = applicants.reduce((obj: Record<string, Financials>, applicant: Applicant) => {
    obj[applicant.id] = mapFinancials(applicant.id, data);
    return obj;
  }, {} as ApplicantFinancials);

  const erhlDetails = data.erhlDetails;
  // Resolve the exit strategy from the active value source (manual override wins, else application value)
  const exitStrategy =
    erhlDetails?.exitStrategyUsed === 'manual_value'
      ? erhlDetails?.exitStrategyManualValue
      : erhlDetails?.exitStrategyApplicationValue;
  const erhlDrawdowns = buildErhlDrawdowns(data);
  const incomingProperties = withErhlPurchaseContribution(
    data,
    data.incomingProperties?.map(mapFromApi.incomingProperty) ?? [],
  );

  return {
    settlementDate: data.settlementDate,
    exitStrategy: exitStrategy as LoanApplication['exitStrategy'],
    exitStrategyDetails: data.exitStrategyDetails,
    additionalFundsRequired: (data.additionalFundsRequired ?? {}) as unknown as AdditionalFundsRequiredDto,
    applicationId: data.id ?? '',
    userType: 'Secondary', // ???
    actionType: 'updateExisting',
    loanType: data.loanType as LoanTypeEnum,
    applicants,
    declaredApplicantIds,
    borrowerGroups: mapFromApi.borrowerGroups(data.borrowers),
    incomingProperties,
    outgoingProperties: data.outgoingProperties?.map(mapFromApi.outgoingProperty) ?? [],
    supportingProperties: data.supportingProperties?.map(mapFromApi.supportingProperty) ?? [],
    propertiesOwnership: mapFromApi.propertyOwnership(data.propertiesOwnership ?? []),
    loanDetails: mapFromApi.additionalFunds(data),
    financials,
    endDebt: data.calculatedValue?.endDebt ?? 0,
    netSurplus: (data.calculatedValue as { netSurplus?: number } | undefined)?.netSurplus ?? 0,
    verificationCode: '',
    brokerId: data.brokerId ?? '',
    structuralVersionNumber: data.structuralVersionNumber ?? 0,
    summaryNotes: data.summaryNotes ?? '',
    utmMetrics: {} as UtmMetricDto,
    solicitor: data.solicitor,
    domainVariables: data.domainVariables ?? [],
    productType: data.productType,
    lendingProduct: data.lendingProduct,
    erhlLoanPurposes: data.lendingProduct === 'erhl' ? erhlDetails?.loanPurposes : undefined,
    erhlDrawdowns,
    erhlEquityAccessMethod: erhlEquityAccessMethod(data, erhlDrawdowns),
  };
};
